import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLanguage, type LanguageCode } from "@/contexts/LanguageContext";
import {
  type CurrencyCode,
  type ExchangeRates,
  getStoredCurrency,
  setStoredCurrency,
  formatDisplayPrice,
  convertPrice,
} from "@/lib/currency";

const LANGUAGE_TO_CURRENCY: Record<LanguageCode, CurrencyCode> = {
  sv: "SEK",
  no: "SEK",
  da: "SEK",
  fi: "EUR",
  de: "EUR",
  nl: "EUR",
  fr: "EUR",
  es: "EUR",
  it: "EUR",
  en: "USD",
};

interface CurrencyContextValue {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  rates: ExchangeRates | null;
  ratesLoading: boolean;
  formatPrice: (amountSEK: number) => string;
  convert: (amountSEK: number) => number;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

async function fetchRates(): Promise<ExchangeRates> {
  const res = await fetch("https://api.frankfurter.dev/v1/latest?base=SEK&symbols=EUR,USD");
  if (!res.ok) throw new Error("Failed to fetch exchange rates");
  const data = await res.json();
  return data.rates as ExchangeRates;
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { language } = useLanguage();
  const userChosen = useRef(false);
  const [currency, setCurrencyState] = useState<CurrencyCode>(getStoredCurrency);

  const { data: rates = null, isLoading: ratesLoading } = useQuery({
    queryKey: ["exchange-rates"],
    queryFn: fetchRates,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 2,
  });

  // Auto-sync currency when language changes (unless user manually picked)
  useEffect(() => {
    if (userChosen.current) return;
    const mapped = LANGUAGE_TO_CURRENCY[language];
    if (mapped && mapped !== currency) {
      setCurrencyState(mapped);
      setStoredCurrency(mapped);
    }
  }, [language]);

  const setCurrency = useCallback((code: CurrencyCode) => {
    userChosen.current = true;
    setCurrencyState(code);
    setStoredCurrency(code);
  }, []);

  const formatPrice = useCallback(
    (amountSEK: number) => formatDisplayPrice(amountSEK, currency, rates),
    [currency, rates],
  );

  const convert = useCallback(
    (amountSEK: number) => convertPrice(amountSEK, currency, rates),
    [currency, rates],
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, ratesLoading, formatPrice, convert }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
